import { mergeAttributes, Node, type JSONContent } from "@tiptap/core"
import * as z from "zod"
import { CARD_COLORS } from "../../util/colors"
import { formatMarkdownAttributes } from "../../util/markdown-json-attributes"
import { BLOCK_SEPARATOR, md } from "../../util/markdown-template"
import { toTiptapAttributes } from "../../util/render-data"
import { CARD_ID_ATTRIBUTE, CardMarkdownAttrsSchema } from "../cards/card-content-extensions"
import { ExtraPostPropertiesSchema, GoogleClassroomPostTypeSchema } from "./google-classroom-attrs"

export {
  GOOGLE_CLASSROOM_POST_TYPES,
  GoogleClassroomPostTypeSchema,
  type GoogleClassroomPostType,
} from "./google-classroom-attrs"

export const CardGoogleClassroomAttrsSchema = z.object({
  id: z.string(),
  parentCardId: z.string().nullable().default(null),
  commentIds: z.array(z.string()).default([]),
  color: z.enum(CARD_COLORS).default("yellow"),
  isPublic: z.boolean().default(false),
  postType: GoogleClassroomPostTypeSchema.default("ASSIGNMENT"),
  title: z.string().default(""),
  value: z.string().default(""),
  maxPoints: z.string().nullable().default(null),
  classSettings: z
    .array(
      z.object({
        googleClassId: z.string(),
        isEnabled: z.boolean(),
        scheduledAtTime: z.object({ hour: z.number(), minute: z.number() }),
        dueAt: z.object({ date: z.string(), hour: z.number(), minute: z.number() }).nullable(),
      }),
    )
    .default([]),
  attachmentSettings: z
    .record(
      z.string(),
      z.object({
        shareMode: z.enum(["VIEW", "EDIT", "STUDENT_COPY"]),
      }),
    )
    .default({}),
  extraPostProperties: ExtraPostPropertiesSchema.default({}),
  enabledGoogleClassIds: z.array(z.string()).default([]),
  attachments: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        url: z.string(),
        type: z.string(),
        mimetype: z.string().nullable(),
        size: z.number().nullable(),
        isWriteable: z.boolean(),
      }),
    )
    .default([]),
  revision: z.number().default(0),
})

export type ExtensionCardGoogleClassroomAttrs = z.output<typeof CardGoogleClassroomAttrsSchema>

export const CardGoogleClassroomContentExtension = Node.create({
  name: "cardGoogleClassroom",
  group: "card addTarget widget",
  atom: true,
  draggable: true,

  addAttributes: () =>
    toTiptapAttributes(CardGoogleClassroomAttrsSchema, { id: CARD_ID_ATTRIBUTE }),

  parseHTML() {
    return [{ tag: 'div[data-content-type="cardGoogleClassroom"]' }]
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as ExtensionCardGoogleClassroomAttrs
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-content-type": "cardGoogleClassroom" }),
      ["h2", {}, attrs.title ?? ""],
      ["p", {}, attrs.value ?? ""],
    ]
  },

  renderMarkdown(node: JSONContent): string {
    const card = CardGoogleClassroomAttrsSchema.parse(node.attrs)
    const attrs = formatMarkdownAttributes(CardMarkdownAttrsSchema, {
      ...card,
      googleClassroom: true,
    })

    return md`
      ## ${card.title} ${attrs}

      ${card.value}
    `
  },
  // parseMarkdown happens in card-stack-markdown.ts instead of here.

  renderText({ node }) {
    const attrs = node.attrs as ExtensionCardGoogleClassroomAttrs
    return [attrs.title, attrs.value].filter(Boolean).join(BLOCK_SEPARATOR)
  },
})
